// components/CustomerStatusBar.tsx
import React from "react";
import {
  Box,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import { useRoles } from "../useRoles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { CustomerDetailFormState, ValidationErrors } from "../types/customer";
import { elevation } from "../theme";
import { isValid } from "date-fns";
import { DesktopAppMenu, SystemType } from "./DesktopAppMenu";
import { SyncStatusMenu, SyncStatus } from "./SyncStatusMenu";

interface CustomerStatusBarProps {
  formData: CustomerDetailFormState;
  validationErrors: ValidationErrors;
  isNew: boolean;
  deceasedInputTempValue: Date | null;
  onInputChange: (e: any) => void;
  onDeceasedDateChange: (newValue: Date | null) => void;
  onDeceasedDateAccept: (newValue: Date | null) => void;
  onDeceasedDateClose: () => void;
  onSystemSelect: (system: SystemType) => void;
  onCheckSyncStatus: () => Promise<SyncStatus>;
  onForceSync: (system: 'hearing' | 'optics') => Promise<void>;
  disableCustomerToggle?: boolean;
  hideDesktopIcon?: boolean;
  showSharedSpace: boolean;
  onShowSharedSpaceChange: (checked: boolean) => void;
}

export const CustomerStatusBar: React.FC<CustomerStatusBarProps> = ({
  formData,
  validationErrors,
  isNew,
  deceasedInputTempValue,
  onInputChange,
  onDeceasedDateChange,
  onDeceasedDateAccept,
  onDeceasedDateClose,
  onSystemSelect,
  onCheckSyncStatus,
  onForceSync,
  disableCustomerToggle,
  hideDesktopIcon,
  showSharedSpace,
  onShowSharedSpaceChange,
}) => {
  const roles = useRoles();
  const isPrivileged = roles.includes('PrivilegedUsers');
  const noPermissionMessage = "You do not have permission to access this option. Please contact your manager or supervisor for advice.";

  return (
    <Paper
      sx={{
        mb: 3,
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 3,
        flexWrap: "wrap",
      }}
      elevation={elevation.paper}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 3, flex: 1 }}>
        {!isNew && !hideDesktopIcon && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DesktopAppMenu onSystemSelect={onSystemSelect} />
            <SyncStatusMenu 
              onCheckSyncStatus={onCheckSyncStatus}
              onForceSync={onForceSync}
            />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Tooltip title={!isPrivileged ? noPermissionMessage : ""}>
            <span>
              <FormControlLabel
                control={
                  <Switch
                    checked={showSharedSpace}
                    onChange={(_, checked) => onShowSharedSpaceChange(checked)}
                    color="primary"
                    disabled={!isPrivileged}
                  />
                }
                label="Shared address"
              />
            </span>
          </Tooltip>
          {/* Hidden: user activation toggle - remove box tag when needed to enable again*/}
          <Box sx={{ display: 'none' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={onInputChange}
                  name="isActive"
                />
              }
              label="Active"
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isCommercialClient}
                onChange={onInputChange}
                name="isCommercialClient"
                disabled={disableCustomerToggle}
              />
            }
            label="Customer"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isDeceased}
                onChange={onInputChange}
                name="isDeceased"
              />
            }
            label="Deceased"
          />
          {formData.isDeceased && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Deceased Date"
                minDate={new Date("1900-01-01")}
                maxDate={new Date()}
                value={deceasedInputTempValue}
                onChange={(newValue) => onDeceasedDateChange(newValue as Date)}
                onAccept={(newValue) => {
                  if (newValue instanceof Date && isValid(newValue)) {
                    onDeceasedDateAccept(newValue);
                  }
                }}
                onClose={() => {
                  if (
                    deceasedInputTempValue instanceof Date &&
                    isValid(deceasedInputTempValue)
                  ) {
                    onDeceasedDateClose();
                  }
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: false,
                    error: Boolean(validationErrors.deceasedDate),
                    helperText: validationErrors.deceasedDate,
                    sx: {
                      minWidth: 180,
                      ...(formData.isDeceased && {
                        backgroundColor: "yellow",
                      }),
                    },
                  },
                }}
              />
            </LocalizationProvider>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
